/**
 * Shared building blocks for the AI Settings hub tabs.
 *
 * These mirror the idioms in the original AiSettings.tsx (model dropdown that
 * keeps an out-of-list current value selectable, a system-prompt textarea with a
 * Custom/Default badge + reset-to-default, and small "Saved." / error rows) so
 * every tab feels identical. Kept presentational — state + save wiring live in
 * each tab.
 */
import type { ReactNode } from "react";
import { Button, Card, SelectField, Badge } from "@/components/ui";

/** Build the model <option> list, keeping a configured-but-unlisted value selectable. */
export function modelOptions(
  options: string[],
  current: string,
): Array<{ value: string; label: string }> {
  const opts = options.map((m) => ({ value: m, label: m }));
  if (current && !options.includes(current)) {
    opts.unshift({ value: current, label: `${current} (current)` });
  }
  return opts;
}

/** A labeled model dropdown (same affordance as the original screen). */
export function ModelCard({
  label,
  help,
  options,
  value,
  onChange,
}: {
  label: string;
  help?: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Card className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold">Model</h2>
      <SelectField
        label={label}
        options={modelOptions(options, value)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="max-w-md"
      />
      {help && <p className="text-sm text-muted-foreground">{help}</p>}
    </Card>
  );
}

/**
 * A system-prompt textarea card: Custom/Default badge, free-text body, a
 * reset-to-default button, and the transient Saved/error row. `isCustom` is
 * driven by the parent (server's *_is_custom or "non-empty after save").
 */
export function SystemPromptCard({
  title,
  value,
  onChange,
  isCustom,
  onReset,
  resetDisabled,
  help,
  rows = 16,
  savedSlot,
  errorSlot,
}: {
  title: string;
  value: string;
  onChange: (v: string) => void;
  isCustom: boolean;
  onReset: () => void;
  resetDisabled: boolean;
  help?: ReactNode;
  rows?: number;
  savedSlot?: ReactNode;
  errorSlot?: ReactNode;
}) {
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">{title}</h2>
        <Badge tone={isCustom ? "primary" : "muted"}>
          {isCustom ? "Custom" : "Default"}
        </Badge>
      </div>

      <label className="flex flex-col gap-1 text-sm">
        <span className="sr-only">{title}</span>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          spellCheck={false}
          placeholder="Leave blank to use the built-in default prompt…"
          className="w-full rounded-md border bg-background px-3 py-2 font-mono text-xs leading-relaxed outline-none focus:ring-2 focus:ring-ring"
        />
      </label>

      {help && <p className="text-sm text-muted-foreground">{help}</p>}

      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={onReset} disabled={resetDisabled}>
          Reset to default
        </Button>
        {savedSlot}
        {errorSlot}
      </div>
    </Card>
  );
}

/** The standard transient "Saved." confirmation. */
export function SavedNote({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
      Saved.
    </span>
  );
}

/** The standard save-failure message. */
export function ErrorNote({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <span className="text-sm text-red-600">
      Couldn&apos;t save your changes. Please try again.
    </span>
  );
}

/** Small square icon button used by the list-editor reorder/delete controls. */
export function IconButton({
  title,
  onClick,
  disabled,
  children,
}: {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md border text-sm transition hover:bg-accent disabled:pointer-events-none disabled:opacity-40"
    >
      {children}
    </button>
  );
}

/** Move an item within an array (returns a new array; no-op if out of range). */
export function moveItem<T>(arr: T[], from: number, to: number): T[] {
  if (to < 0 || to >= arr.length || from === to) return arr;
  const next = arr.slice();
  const [item] = next.splice(from, 1);
  if (item === undefined) return arr;
  next.splice(to, 0, item);
  return next;
}

/** A plain labeled number input (the ui.TextField is fine but this keeps callbacks numeric). */
export function NumberField({
  label,
  value,
  onChange,
  min,
  className,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  className?: string;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium">{label}</span>
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        min={min}
        onChange={(e) => {
          const n = e.target.valueAsNumber;
          onChange(Number.isNaN(n) ? 0 : n);
        }}
        className={
          "rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring " +
          (className ?? "")
        }
      />
    </label>
  );
}
